import * as THREE from 'three';
import { GeoBuilder } from './geo.js';
import { Kit } from './buildings.js';
import { rr, rnd, pick, chance, TAU } from './util.js';

/** Small street furniture, all built in local space (origin on the ground, front = +Z). */

export function buildLamp() {
  const K = new Kit();
  K.body.cyl(0.16, 0.22, 0.4, 8, 0, 0, 0, '#3b3f4f');
  K.body.cyl(0.07, 0.11, 3.3, 6, 0, 0.3, 0, '#3b3f4f');
  K.body.box(0.5, 0.08, 0.08, 0.2, 3.5, 0, '#3b3f4f');
  K.body.box(0.44, 0.08, 0.44, 0.5, 3.1, 0, '#2c2f3d');
  K.glow.box(0.32, 0.5, 0.32, 0.5, 3.16, 0, '#ffffff');
  K.body.cone(0.36, 0.3, 4, 0.5, 3.66, 0, '#2c2f3d', { ry: Math.PI / 4 });
  return K;
}

export function buildBench() {
  const K = new Kit();
  const wood = '#a36a43', dark = '#3b3f4f';
  K.body.box(2.0, 0.09, 0.55, 0, 0.5, 0, wood);
  K.body.box(2.0, 0.5, 0.08, 0, 0.75, -0.28, wood, { rx: -0.15 });
  for (const s of [-1, 1]) {
    K.body.box(0.08, 0.5, 0.5, s * 0.85, 0.0, 0, dark);
    K.body.box(0.08, 0.35, 0.08, s * 0.85, 0.5, -0.27, dark);
  }
  return K;
}

export function buildStall(awning1 = '#e94f4f', awning2 = '#fff6e6') {
  const K = new Kit();
  const wood = '#8b5a3c';
  K.body.box(2.8, 0.9, 1.3, 0, 0, 0, wood, { jitter: 0.06 });
  K.body.box(3.0, 0.1, 1.5, 0, 0.9, 0, '#a87a52');
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) K.body.box(0.12, 2.6, 0.12, sx * 1.4, 0, sz * 0.72 - 0.05 * sz, '#6b4a33');
  // striped awning (tilted forward)
  const n = 8;
  for (let i = 0; i < n; i++) {
    const x = -1.75 + (i + 0.5) * (3.5 / n);
    K.body.boxC(3.5 / n, 0.07, 2.0, x, 2.55, 0.15, i % 2 ? awning2 : awning1, { rx: 0.22 });
    K.body.boxC(3.5 / n, 0.22, 0.07, x, 2.32, 1.1, i % 2 ? awning2 : awning1, { rx: 0 });
  }
  // goods
  const fruit = ['#e63946', '#f4a261', '#ffd166', '#8ac926', '#c77dff'];
  for (let i = 0; i < 3; i++) {
    const x = -0.95 + i * 0.95;
    K.body.box(0.75, 0.22, 0.6, x, 1.0, 0.1, '#c99a68');
    const col = fruit[(i + Math.floor(rnd() * 5)) % 5];
    for (let k = 0; k < 7; k++) K.body.ico(0.13, 0, x + rr(-0.26, 0.26), 1.28, 0.1 + rr(-0.2, 0.2), col, { jitter: 0.08 });
  }
  return K;
}

export function buildBarrel() {
  const K = new Kit();
  K.body.cyl(0.42, 0.36, 0.95, 8, 0, 0, 0, '#8b5a3c', { jitter: 0.06 });
  K.body.cyl(0.44, 0.44, 0.08, 8, 0, 0.22, 0, '#4a4a55');
  K.body.cyl(0.44, 0.44, 0.08, 8, 0, 0.66, 0, '#4a4a55');
  return K;
}

export function buildCrate() {
  const K = new Kit();
  K.body.box(0.8, 0.7, 0.8, 0, 0, 0, '#c99a68', { jitter: 0.08 });
  K.body.box(0.86, 0.08, 0.86, 0, 0.62, 0, '#a87a52');
  return K;
}

export function buildHayBale() {
  const K = new Kit();
  K.body.cyl(0.75, 0.75, 1.1, 10, 0, 0, 0, '#e6c452', { rx: Math.PI / 2, jitter: 0.1 });
  return K;
}

export function buildSignpost() {
  const K = new Kit();
  K.body.box(0.14, 2.4, 0.14, 0, 0, 0, '#6b4a33');
  K.body.box(1.3, 0.34, 0.06, 0.5, 1.8, 0.05, '#e9d7b0', { ry: 0.1 });
  K.body.box(1.1, 0.34, 0.06, -0.4, 1.35, 0.05, '#d9603b', { ry: -0.15 });
  return K;
}

export function buildScarecrow() {
  const K = new Kit();
  K.body.box(0.12, 2.3, 0.12, 0, 0, 0, '#6b4a33');
  K.body.box(1.7, 0.1, 0.1, 0, 1.55, 0, '#6b4a33');
  K.body.cyl(0.22, 0.4, 0.95, 7, 0, 0.95, 0, '#4a6aa8', { jitter: 0.06 });
  K.body.sphere(0.32, 0, 2.05, 0, '#f1d08a');
  K.body.cyl(0.34, 0.34, 0.06, 8, 0, 2.28, 0, '#7a5a35');
  K.body.cyl(0.2, 0.28, 0.32, 8, 0, 2.3, 0, '#7a5a35');
  for (const s of [-1, 1]) K.body.box(0.14, 0.5, 0.1, s * 0.85, 1.05, 0, '#e6c452', { rz: s * 0.2, jitter: 0.1 });
  K.body.box(0.05, 0.05, 0.05, -0.1, 2.12, 0.3, '#222');
  K.body.box(0.05, 0.05, 0.05, 0.1, 2.12, 0.3, '#222');
  return K;
}

/** Wood fence along a polyline of {x,z,y}. Returns geometry parts into `b` (world space). */
export function addFence(b, pts, { closed = false, color = '#f3ead6', post = '#b89468', height = 1.0, spacing = 1.9, picket = true } = {}) {
  const n = pts.length;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const a = pts[i], c = pts[(i + 1) % n];
    const len = Math.hypot(c.x - a.x, c.z - a.z);
    if (len < 0.2) continue;
    const yaw = Math.atan2(c.x - a.x, c.z - a.z);
    const count = Math.max(1, Math.round(len / spacing));
    const step = len / count;
    for (let k = 0; k < count; k++) {
      const t0 = k / count, t1 = (k + 1) / count;
      const x0 = a.x + (c.x - a.x) * t0, z0 = a.z + (c.z - a.z) * t0, y0 = a.y + (c.y - a.y) * t0;
      const x1 = a.x + (c.x - a.x) * t1, z1 = a.z + (c.z - a.z) * t1, y1 = a.y + (c.y - a.y) * t1;
      b.box(0.14, height + 0.15, 0.14, x0, y0 - 0.15, z0, post, { jitter: 0.06 });
      const pitch = Math.atan2(y1 - y0, step);
      for (const ry of picket ? [0.32, 0.72] : [0.35, 0.75]) {
        b.boxC(0.07, 0.11, step, (x0 + x1) / 2, (y0 + y1) / 2 + ry, (z0 + z1) / 2, color, { ry: yaw, rx: -pitch });
      }
    }
  }
  const last = closed ? pts[0] : pts[n - 1];
  b.box(0.14, height + 0.15, 0.14, last.x, last.y - 0.15, last.z, post);
}

export function addHedge(b, pts, { height = 1.1, color = '#3f9a3f' } = {}) {
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], c = pts[i + 1];
    const len = Math.hypot(c.x - a.x, c.z - a.z);
    const n = Math.max(1, Math.round(len / 1.15));
    for (let k = 0; k <= n; k++) {
      const t = k / n;
      const x = a.x + (c.x - a.x) * t, z = a.z + (c.z - a.z) * t, y = a.y + (c.y - a.y) * t;
      const jc = new THREE.Color(color).offsetHSL(rr(-0.02, 0.02), 0, rr(-0.05, 0.06));
      b.ico(rr(0.62, 0.78), 1, x + rr(-0.12, 0.12), y + height * 0.45, z + rr(-0.12, 0.12), jc, { sy: 0.85, jitter: 0.14 });
    }
  }
}
